import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { format } from 'app/shared/utils/string.util';
import { GlobalVariables } from 'app/services/globals';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { IDataflowTypeMetadata } from '../../extensibility/metadatas/resource-type/dataflow-format-type-metadata';

const dataflowAdobeCampaign: IDataflowTypeMetadata = {
    type: DatasetFormatType.AdobeCampaign,
    displayName: stringResources.DataFlowSourcePanelAdobeCampaign,
    icon: {
        className: 'svg-i-20x20-adobe-campaign'
    },
    settings: adfModelTypeMap.adobeCampaign.dataflowSettings
};

const dataflowAdobeIOEvents: IDataflowTypeMetadata = {
    type: DatasetFormatType.AdobeIOEvents,
    displayName: stringResources.DataFlowSourcePanelAdobeIOEvents,
    settings: adfModelTypeMap.adobeIOEvents.dataflowSettings,
    controls: [
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'lastPosition',
                label: stringResources.LastPosition,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            isDataFlowExpression: true,
            expectedReturnType: 'string',
            showErrorIcon: false
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'batchSize',
                label: stringResources.ModelName_BatchSize,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            isDataFlowExpression: true,
            expectedReturnType: 'integer',
            showErrorIcon: false
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'requestInterval',
                label: stringResources.ModelName_REST_Source_Request_Interval,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            isDataFlowExpression: true,
            expectedReturnType: 'integer',
            showErrorIcon: false
        }
    ]
};

const dataflowAdobePrivacy: IDataflowTypeMetadata = {
    type: DatasetFormatType.AdobePrivacy,
    displayName: stringResources.DataFlowSourcePanelAdobePrivacyService,
    settings: adfModelTypeMap.adobePrivacy.dataflowSettings
};

export const adobeIntegrationMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.adobeIntegration.linkedServiceType,
    displayName: stringResources.AdobeIntegration_DisplayText,
    linkedServiceKind: 'file',
    icon: {
        className: 'svg-i-20x20-adobe-integration'
    },
    onlyAzureIR: true,
    isFirstClass: true,
    category: ['ServicesApps'],
    copySinkTypes: [],
    copySourceTypes: [],
    fileNameDisplay: 'dataset id',
    notSupportCompression: true,
    disableQueryAPI: true,
    supportDataFlowSource: () => GlobalVariables.enableAdobeIntegration,
    supportDataFlowSink: () => GlobalVariables.enableAdobeIntegration,
    dataflowSource: [dataflowAdobeCampaign, dataflowAdobeIOEvents],
    dataflowSink: [dataflowAdobeCampaign, dataflowAdobePrivacy],
    controls: [
        {
            type: 'textbox',
            bind: 'apiKey',
            label: stringResources.ModelName_APIKey,
            description: stringResources.AdobeExperiencePlatform_ApiKeyDescription,
            placeholder: stringResources.AdobeIntegration_APIkeyPlaceholder,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true
        },
        {
            type: 'textbox',
            bind: 'accountId',
            label: stringResources.ModelName_AccountID,
            description: stringResources.AdobeExperiencePlatform_AccountIdDescription,
            placeholder: stringResources.AdobeIntegration_AccountIdPlaceholder,
            isLabelVertical: true,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ]
        },
        {
            type: 'textbox',
            bind: 'orgId',
            label: stringResources.ModelName_OrgID,
            description: stringResources.AdobeExperiencePlatform_OrgIdDescription,
            placeholder: stringResources.AdobeIntegration_OrgIdPlaceholder,
            isLabelVertical: true,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ]
        },
        {
            type: 'textbox',
            bind: 'tenantId',
            label: stringResources.ModelName_TenantId,
            description: stringResources.AdobeIntegration_TenantIdDescription,
            placeholder: stringResources.AdobeIntegration_TenantIdPlaceholder,
            isLabelVertical: true,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ]
        },
        {
            type: 'textbox',
            bind: 'exp',
            label: stringResources.ModelName_Exp,
            description: stringResources.AdobeIntegration_ExpDescrption,
            placeholder: stringResources.AdobeIntegration_ExpPlaceholder,
            isLabelVertical: true,
            required: false,
            defaultValue: 60,
            validators: [
                { type: 'integer' }
            ]
        },
        {
            type: 'multi-select',
            bind: 'metaScopes',
            label: stringResources.ModelName_MetaScopes,
            options: [
                {
                    label: stringResources.DataFlowSourcePanelAdobeCampaign,
                    value: 'ent_campaign_sdk'
                },
                {
                    label: stringResources.AdobeIntegration_EventsMetaScope,
                    value: 'ent_adobeio_sdk'
                },
                {
                    label: stringResources.AdobeIntegration_IOManagementMetaScope,
                    value: 'event_receiver_api'
                },
                {
                    label: stringResources.AdobeIntegration_PrivacyMetaScope,
                    value: 'ent_gdpr_sdk'
                }
            ],
            description: stringResources.AdobeIntegration_MetaScopesDescrption,
            isLabelVertical: true,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ]
        },
        {
            type: 'textbox',
            bind: 'journalEndpoint',
            label: stringResources.ModelName_JournalEndpoint,
            description: stringResources.AdobeIntegration_JournalApiEndpointDescription,
            placeholder: stringResources.AdobeIntegration_JournalEndpointPlaceholder,
            isLabelVertical: true,
            required: false
        },
        {
            type: 'dynamic-akv-switcher',
            bind: 'clientSecret',
            label: stringResources.ModelName_ClientSecret,
            child: {
                type: 'password',
                label: stringResources.ModelName_ClientSecret,
                description: stringResources.AdobeExperiencePlatform_ClientSecretDescription,
                required: true,
                isLabelVertical: true
            },
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AdobeExperiencePlatform_DisplayText),
                stringResources.ModelName_ClientSecret)
        },
        {
            type: 'dynamic-akv-switcher',
            bind: 'privateKeyContent',
            label: stringResources.ModelName_PrivateKeyContent,
            child: {
                type: 'key-file-content',
                label: stringResources.ModelName_PrivateKeyContent,
                readAsText: true,
                description: stringResources.AdobeExperiencePlatform_PrivateKeyContentDescription,
                required: true,
                isLabelVertical: true
            },
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AdobeExperiencePlatform_DisplayText),
                stringResources.ModelName_PrivateKeyContent)
        }
    ]
};
